/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

/**
 * Utility functions for image handling
 */

/** Maximum image size in bytes (500KB) */
export const MAX_IMAGE_SIZE = 500_000;

/**
 * Fetches an image from a URL and converts it to a data URL
 * Uses Image element and canvas to bypass CORS issues
 * @param url The URL of the image to fetch
 * @returns Promise<string> The data URL of the image, or empty string on error
 */
export const fetchImageAsDataUrl = async (url: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();

    // Set crossOrigin to allow canvas to read the image
    img.crossOrigin = 'anonymous';

    img.onerror = () => {
      console.error('Error loading image from URL:', url);
      resolve(''); // Return empty string on error
    };

    img.onload = () => {
      try {
        // Create a canvas and draw the image to it
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('Could not get canvas context');
          resolve('');
          return;
        }

        ctx.drawImage(img, 0, 0);

        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL('image/webp', 0.92);
        canvas.remove();

        resolve(dataUrl);
      } catch (error) {
        console.error('Error converting image to data URL:', error);
        resolve(''); // Return empty string on error
      }
    };

    img.src = url;
  });
};

/**
 * Converts an image to WebP format (which is 25-35% smaller than PNG while preserving transparency)
 * @param dataUrl The image data URL to convert
 * @param callback Callback function that receives the converted WebP data URL
 * @param quality WebP quality (0-1), default 0.92
 */
export const convertToWebP = (dataUrl: string, callback: (webpDataUrl: string) => void, quality: number = 0.92): void => {
  // Validate data URL format
  if (!dataUrl.startsWith('data:image/')) {
    console.error('Invalid image data URL');
    callback('');
    return;
  }

  const img = new Image();
  img.onerror = () => {
    console.error('Error loading image for WebP conversion');
    callback('');
  };

  img.onload = () => {
    try {
      const canvas = window.document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Could not get canvas context');
        callback('');
        return;
      }

      ctx.drawImage(img, 0, 0);

      // Convert to WebP format with transparency support
      const webpDataUrl = canvas.toDataURL('image/webp', quality);
      canvas.remove();

      // WebP is usually smaller, but in rare cases it might be larger - use the smaller one
      callback(webpDataUrl.length < dataUrl.length ? webpDataUrl : dataUrl);
    } catch (error) {
      console.error('Error converting to WebP:', error);
      callback(dataUrl); // Fallback to original if conversion fails
    }
  };

  img.src = dataUrl;
};

/**
 * Scales down an image if it exceeds MAX_IMAGE_SIZE
 * @param dataUrl The image data URL to scale down
 * @param callback Callback function that receives the scaled down data URL
 */
export const scaleDownImage = (dataUrl: string, callback: (resizedDataUrl: string) => void): void => {
  // Validate data URL format
  if (!dataUrl.startsWith('data:image/')) {
    console.error('Invalid image data URL');
    callback(''); // Return empty string on error
    return;
  }

  const img = new Image();
  img.onerror = () => {
    console.error('Error loading image data URL');
    callback(''); // Return empty string on error
  };

  if (dataUrl.length <= MAX_IMAGE_SIZE) {
    // Image was already smaller than max size, but still validate
    img.onload = () => callback(dataUrl);
  } else {
    // Set timeout to prevent long-running operations
    const timeout = setTimeout(() => {
      console.error('Image processing timed out');
      callback('');
    }, 10000); // 10 second timeout

    img.onload = () => {
      try {
        clearTimeout(timeout);
        const ratio = MAX_IMAGE_SIZE / dataUrl.length;
        let perAxis = Math.sqrt(ratio);
        let resizedDataUrl = dataUrl;
        let attemptsLeft = 20; // Prevent infinite loops
        const canvas = window.document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        while (ctx && resizedDataUrl.length > MAX_IMAGE_SIZE && attemptsLeft--) {

          console.log(`reducing image size by ${perAxis*100}%`);

          const width = Math.max(1, Math.round(img.width * perAxis));
          const height = Math.max(1, Math.round(img.height * perAxis));

          canvas.width = width;
          canvas.height = height;

          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Use 'image/webp' to preserve transparency while reducing file size
          resizedDataUrl = canvas.toDataURL('image/webp', 0.92);

          // In case we have to repeat this while loop, make the resulting image 10% smaller on each axis.
          // We always use img as our source, which does not change, so there are no compounding errors.
          perAxis *= 0.9;
        }
        canvas.remove();
        if (resizedDataUrl.length > MAX_IMAGE_SIZE) {
          console.error('Unable to resize image.');
          callback('');
        } else {
          callback(resizedDataUrl);
        }
      } catch (error) {
        console.error('Error during image resizing:', error);
        callback('');
      }
    };
  }
  img.src = dataUrl;
};

/**
 * Converts an image (data URL or URL) to a WebP Blob for file export
 * @param imageSrc The image source (data URL or URL)
 * @param quality WebP quality (0-1), default 0.9
 * @returns Promise<Blob | null> The WebP blob, or null on error
 */
export const convertImageToWebpBlob = async (imageSrc: string, quality: number = 0.9): Promise<Blob | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/webp', quality);
    };

    img.onerror = () => {
      console.error('Failed to load image for webp conversion');
      resolve(null);
    };

    img.src = imageSrc;
  });
};
